import { useState, type ReactNode } from 'react';
import { SbIcon } from './sidebarIcons';
import '../styles/reports.css';

/**
 * Icon and tint per module picture. The picture, when one exists at
 * /report-icons/<name>.png, sits over the line icon; without it the icon is the
 * whole tile, so a missing file never shows as a broken image. Shared by the
 * Reports folders and the Dashboard module tray so they are the same pictures.
 */
export const FOLDER_ART: Record<string, { icon: string; tint: string; image?: string }> = {
  Property: { icon: 'properties', tint: '#0F8275' },
  Tenants: { icon: 'tenants', tint: '#3F6E8C' },
  Rent: { icon: 'rent', tint: '#0F8275' },
  Financial: { icon: 'reports', tint: '#B8862B' },
  Maintenance: { icon: 'operations', tint: '#9A5B3F' },
  Deposits: { icon: 'deposits', tint: '#5B6B8C' },
  Compliance: { icon: 'compliance', tint: '#4F7A5A' },
  Utilities: { icon: 'utilities', tint: '#2C7D9C' },
  // Dashboard tray shortcuts. Each looks for /report-icons/<image>.png and shows
  // the drawn icon until that file exists. The "Quick-" prefix keeps the keys
  // apart from the Reports folder names above.
  'Quick-invoices': { icon: 'invoices', tint: '#3F6E8C', image: 'invoices' },
  'Quick-arrears': { icon: 'arrears', tint: '#A8541F', image: 'arrears' },
  'Quick-reconcile': { icon: 'reconcile', tint: '#0F8275', image: 'reconcile' },
  'Quick-leases': { icon: 'leases', tint: '#8C4F6B', image: 'leases' },
  'Quick-mpesa': { icon: 'mpesa', tint: '#2E7D4F', image: 'mpesa' },
  'Quick-creditnotes': { icon: 'creditnote', tint: '#5B6B8C', image: 'creditnotes' },
  // Dashboard Quick actions. Where a picture already ships it is shared; the
  // rest are the five added with the Quick action tiles.
  'Act-payment': { icon: 'paymentsin', tint: '#0F8275', image: 'rent' },
  'Act-generate': { icon: 'rentroll', tint: '#0F8275', image: 'generaterent' },
  'Act-reminders': { icon: 'bell', tint: '#A8541F', image: 'reminders' },
  'Act-tenant': { icon: 'tenants', tint: '#3F6E8C', image: 'tenants' },
  'Act-movein': { icon: 'tenancies', tint: '#8C4F6B', image: 'movein' },
  'Act-unit': { icon: 'units', tint: '#5B6B8C', image: 'editunit' },
  'Act-invoice': { icon: 'invoices', tint: '#3F6E8C', image: 'invoices' },
  'Act-maintenance': { icon: 'operations', tint: '#9A5B3F', image: 'maintenance' },
  'Act-meters': { icon: 'utilities', tint: '#2C7D9C', image: 'utilities' },
  'Act-leases': { icon: 'leases', tint: '#8C4F6B', image: 'leases' },
  'Act-calendar': { icon: 'calendar', tint: '#4F7A5A', image: 'calendar' },
  'Act-reports': { icon: 'reports', tint: '#B8862B', image: 'financial' },
  // Pictures for the modules that had none: owners, insights, settings and so on.
  owners: { icon: 'owners', tint: '#B8862B', image: 'owners' },
  insights: { icon: 'intelligence', tint: '#0F8275', image: 'insights' },
  settings: { icon: 'settings', tint: '#5B6B8C', image: 'settings' },
  integrations: { icon: 'data', tint: '#2C7D9C', image: 'integrations' },
  documents: { icon: 'receipt', tint: '#3F6E8C', image: 'documents' },
  portfolio: { icon: 'portfolio', tint: '#0F8275', image: 'portfolio' },
  agreements: { icon: 'leases', tint: '#8C4F6B', image: 'agreements' },
  screening: { icon: 'compliance', tint: '#4F7A5A', image: 'screening' },
  approvals: { icon: 'check', tint: '#4F7A5A', image: 'approvals' },
  automation: { icon: 'operations', tint: '#9A5B3F', image: 'automation' },
  analytics: { icon: 'reports', tint: '#B8862B', image: 'analytics' },
  units: { icon: 'units', tint: '#5B6B8C', image: 'units' },
  // Home page tiles that had no picture of their own.
  inspections: { icon: 'inspections', tint: '#4F7A5A', image: 'inspections' },
  expenses: { icon: 'receipt', tint: '#9A5B3F', image: 'expenses' },
  messages: { icon: 'mail', tint: '#3F6E8C', image: 'messages' },
  vacancies: { icon: 'vacancies', tint: '#0F8275', image: 'vacancies' },
  forsale: { icon: 'sale', tint: '#B8862B', image: 'forsale' },
  dataio: { icon: 'data', tint: '#2C7D9C', image: 'dataio' },
  agents: { icon: 'intelligence', tint: '#0F8275', image: 'agents' },
  alerts: { icon: 'bell', tint: '#B8862B', image: 'alerts' },
  dashboard: { icon: 'dashboard', tint: '#0F8275', image: 'dashboard' },
};

export function FolderArt({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'tile' }) {
  const art = FOLDER_ART[name] ?? { icon: 'tenancies', tint: '#0F8275' };
  const [img, setImg] = useState(true);
  return (
    <span className={size === 'sm' ? 'fa fa-sm' : size === 'tile' ? 'fa fa-tile' : 'fa'} style={{ ['--tint' as string]: art.tint }} aria-hidden>
      <SbIcon name={art.icon} size={size === 'sm' ? 22 : size === 'tile' ? 30 : 26} />
      {img && <img src={`/report-icons/${art.image ?? name.toLowerCase()}.png`} alt="" loading="lazy"
        onError={() => setImg(false)} />}
    </span>
  );
}

/**
 * The title block of a page head: the module picture beside the title and the
 * one-line description. Replaces the bare <div><h1/><p/></div> each page repeated.
 */
export function PageTitle({ art, title, children }: { art: string; title: string; children?: ReactNode }) {
  return (
    <div className="page-id">
      <FolderArt name={art} size="sm" />
      <div>
        <h1 className="page-title">{title}</h1>
        {children != null && <p className="page-sub">{children}</p>}
      </div>
    </div>
  );
}
